/**
 * js/api_external.js — communication with EXTERNAL backends / identity providers.
 *
 * This is deliberately separate from api.js (our own backend). Here live the
 * third-party flows: Google & Kakao OAuth. Two patterns are supported per provider:
 *
 *   1) REDIRECT flow stub:
 *        oauth.google.startRedirect()  → provider login → callback handling still
 *        requires backend token/code verification before production use.
 *
 *   2) CLIENT TOKEN flow — convenient for SPAs:
 *        get an id_token / auth code in the browser via the provider SDK, then hand
 *        it to OUR backend after provider verification is implemented.
 *
 * Configure client IDs / redirect URIs in config/config.js → OAUTH.
 *
 * ES module. Import:  import oauth from "./js/api_external.js";
 */
import CONFIG from "../config/config.js";
import { api } from "./api.js";
import { qs, sleep } from "../common/utils.js";

/* Load an external <script> once, resolving when ready. */
const _loaded = {};
function loadScript(src) {
  if (_loaded[src]) return _loaded[src];
  _loaded[src] = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src; s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load " + src));
    document.head.appendChild(s);
  });
  return _loaded[src];
}

/* CSRF state token for redirect flows. */
function makeState() {
  const s = Math.random().toString(36).slice(2) + Date.now().toString(36);
  try { sessionStorage.setItem("prompto.oauth.state", s); } catch (e) {}
  return s;
}
export function verifyState(returned) {
  try { return returned && returned === sessionStorage.getItem("prompto.oauth.state"); }
  catch (e) { return false; }
}

/* ───────────────────────────  GOOGLE  ───────────────────────────── */
const google = {
  /** Build the Google OAuth consent URL (redirect flow). */
  buildAuthUrl(extra = {}) {
    const c = CONFIG.OAUTH.google;
    return c.authBase + qs({
      client_id: c.clientId,
      redirect_uri: c.redirectUri,
      response_type: "code",
      scope: c.scope,
      access_type: "offline",
      include_granted_scopes: "true",
      state: makeState(),
      ...extra,
    });
  },

  /** Redirect the browser to Google. Your backend handles the callback. */
  startRedirect(extra) { window.location.href = this.buildAuthUrl(extra); },

  /**
   * Client-token flow via Google Identity Services. Loads GIS, prompts the user,
   * and exchanges the resulting id_token with OUR backend. Resolves to the user.
   */
  async signIn() {
    const c = CONFIG.OAUTH.google;
    await loadScript(c.gisSrc);
    const idToken = await new Promise((resolve, reject) => {
      try {
        /* global google */
        window.google.accounts.id.initialize({
          client_id: c.clientId,
          callback: (resp) => resp && resp.credential ? resolve(resp.credential) : reject(new Error("No credential")),
        });
        window.google.accounts.id.prompt();
      } catch (e) { reject(e); }
    });
    return api.auth.loginWithProvider("google", { idToken });
  },

  /** Exchange a redirect `code` (if you do client-side exchange) with our backend. */
  async completeRedirect({ code, state }) {
    if (state && !verifyState(state)) throw new Error("OAuth state mismatch");
    return api.auth.loginWithProvider("google", { code });
  },
};

/* ────────────────────────────  KAKAO  ───────────────────────────── */
const kakao = {
  /** Build the Kakao OAuth authorize URL (redirect flow). */
  buildAuthUrl(extra = {}) {
    const c = CONFIG.OAUTH.kakao;
    return c.authBase + qs({
      client_id: c.clientId,
      redirect_uri: c.redirectUri,
      response_type: "code",
      scope: c.scope,
      state: makeState(),
      ...extra,
    });
  },

  startRedirect(extra) { window.location.href = this.buildAuthUrl(extra); },

  /** Client flow via the Kakao JS SDK → authorize → hand the code to our backend. */
  async signIn() {
    const c = CONFIG.OAUTH.kakao;
    await loadScript(c.sdkSrc);
    if (window.Kakao && !window.Kakao.isInitialized()) window.Kakao.init(c.jsKey);
    const auth = await new Promise((resolve, reject) => {
      window.Kakao.Auth.authorize({ redirectUri: c.redirectUri, scope: c.scope });
      // authorize() redirects; in SDK popup setups, wire success/fail here instead.
      setTimeout(() => reject(new Error("Kakao authorize did not complete")), 0);
    }).catch((e) => { throw e; });
    return auth;
  },

  /** Exchange a redirect `code` with our backend. */
  async completeRedirect({ code, state }) {
    if (state && !verifyState(state)) throw new Error("OAuth state mismatch");
    return api.auth.loginWithProvider("kakao", { code });
  },
};

export const oauth = { google, kakao, verifyState };
export default oauth;
